/**
 * KOA-18448 — THE LIVE VALIDATOR DOES NOT RE-CHECK THE WHOLE PROCESS AFTER EVERY LINE.
 *
 * ## The measured defect
 *
 * Live `7cf6ee92`: a ninety-minute build, fifty-six minutes of it inside `ui_workflow_dsl` over
 * thirty calls. The twenty-eight calls whose script contained a `LINT` had a median of 77 seconds;
 * the two without had two seconds each. And the cost did not scale with the diagram — one call
 * spent 101 seconds on a SEVEN-element diagram — so it was never the size of the graph.
 *
 * It is the number of times the graph is validated. `bpmn-js-bpmnlint` re-lints the WHOLE
 * definitions tree on every `elements.changed`, and its own comment states it without qualifying
 * it: *"Update overlays. Always lint and check whether overlays need update or not."* `update()`
 * does not consult the module's own active flag, so toggling the linter off does not stop it —
 * that switch only decides whether the icons are drawn.
 *
 * That run's scripts issued 364 canvas-changing commands against 28 explicit validations, so the
 * process was re-validated at least thirteen times for every answer anybody read. Our own step
 * baking adds more: it fires `elements.changed` on the root after every command-stack change and,
 * for the first minute, from a poll that fires it for EVERY element on the diagram.
 *
 * ## Why suspending changes nothing about what is checked
 *
 * The result of all that re-validation reaches nobody. It paints issue icons on a canvas that, for
 * the length of an AI build, no one is looking at. The answer the author and the completion
 * verdict are read from comes from the `LINT` command, and that command does NOT reuse any of it:
 * it builds its own linter and re-reads the definitions from scratch
 * (`Linting.prototype.lint`). So the finished process is validated by exactly the same check,
 * returning exactly the same answer — what goes away is the same check being recomputed and
 * discarded hundreds of times while the script is still writing.
 *
 * ## What this does, and what it refuses to do
 *
 *  - Suspends ONLY the change-driven pass, and only for the length of one script. The `LINT`
 *    command runs untouched, whether it sits in the middle of the script or at its end.
 *  - Restores on EVERY exit, including a throw — the caller pairs it in a `finally`.
 *  - Runs the live pass ONCE on release, so the human's canvas shows the finished state rather
 *    than whatever was last painted before the script began.
 *  - Restores onto the modeler it patched and no other. A canvas that was replaced mid-script
 *    (§37's case) leaves the new one alone: patching it back would write our closure onto a module
 *    we never suspended.
 *  - Does nothing at all when the modeler or its linting module is absent — a missing canvas is
 *    the caller's error to report, never this module's to hide.
 *
 * Nesting is counted, so an inner script cannot release the outer one's suspension.
 */

interface LintingModuleLike {
  update?: () => void;
}

interface ModelerLike {
  get?: (name: string, strict?: boolean) => unknown;
}

/** The module we patched, so release can refuse to touch anything else. */
let suspendedLinting: LintingModuleLike | undefined;
/** The real `update`, kept so it can be put back exactly as it was. */
let liveUpdate: (() => void) | undefined;
/** Nesting depth — an inner script must not release the outer script's suspension. */
let depth: number = 0;

function lintingModule(): LintingModuleLike | undefined {
  try {
    const modeler: ModelerLike | undefined = (window as unknown as { bpmnModeler?: ModelerLike }).bpmnModeler;
    if (!modeler || typeof modeler.get !== "function") return undefined;
    const linting: unknown = modeler.get("linting", false);
    if (linting === null || typeof linting !== "object") return undefined;
    return linting as LintingModuleLike;
  } catch {
    return undefined;
  }
}

/** Stop the change-driven validation pass for the length of one script. Safe to nest. */
export function beginLiveLintSuspension(): void {
  depth += 1;
  if (depth > 1 || suspendedLinting !== undefined) return;
  const linting: LintingModuleLike | undefined = lintingModule();
  if (linting === undefined || typeof linting.update !== "function") return;
  liveUpdate = linting.update.bind(linting);
  linting.update = (): void => {};
  suspendedLinting = linting;
}

/**
 * Put the live pass back and run it once, so the canvas a person looks at is the finished one.
 *
 * Best-effort by construction: a failure here must never turn an applied script into an error, and
 * the answer the author reads comes from the `LINT` command rather than from this pass.
 */
export function endLiveLintSuspension(): void {
  depth = Math.max(0, depth - 1);
  if (depth > 0) return;
  const linting: LintingModuleLike | undefined = suspendedLinting;
  const restore: (() => void) | undefined = liveUpdate;
  suspendedLinting = undefined;
  liveUpdate = undefined;
  if (linting === undefined || restore === undefined) return;
  // Only the module we actually patched — a replaced canvas keeps its own.
  if (lintingModule() !== linting) return;
  try {
    linting.update = restore;
    restore();
  } catch {
    /* the canvas's own painting is never worth failing an applied script for */
  }
}

/** Test seam — the suspension is module state, and a test must be able to start from none. */
export function resetLiveLintSuspensionForTests(): void {
  if (suspendedLinting !== undefined && liveUpdate !== undefined) {
    try {
      suspendedLinting.update = liveUpdate;
    } catch {
      /* nothing to restore onto */
    }
  }
  suspendedLinting = undefined;
  liveUpdate = undefined;
  depth = 0;
}
